import { ComponentProps, useMemo } from 'react';
import styled from 'styled-components';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Close } from '@assets/icons';
import { Button } from '@components/Button';
import { Modal } from '@components/Modal';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { useApplicationQuestions } from '@hooks/jobsBoard/useApplyToApplicationJob/useApplyToApplicationJob';
import { UseModalVisibilityHookReturn } from '@hooks/shared/useModalVisibility';
import { getApplicationDeadlineInfo } from '@lib/utils/applicationHelper';

import { PostHeader } from '../../components/PostHeader';

import { SubmitJobQuestions } from './SubmitJobQuestions';

type ApplicationSubmitModalProps = {
  applicationSubmitModalVisibility: UseModalVisibilityHookReturn;
  jobPost: JobsBoardApplication;
  loggedInUserId: number;
  publicApplicationData: PublicApplication | undefined;
} & ComponentProps<typeof Modal>;
export const ApplicationSubmitModal = ({
  applicationSubmitModalVisibility,
  jobPost,
  loggedInUserId,
  publicApplicationData,

  ...props
}: ApplicationSubmitModalProps) => {
  return (
    <StyledModal
      centered
      open={applicationSubmitModalVisibility?.isVisible}
      onCancel={applicationSubmitModalVisibility?.hideModal}
      footer={null}
      {...props}
    >
      <ModalContent
        applicationSubmitModalVisibility={applicationSubmitModalVisibility}
        jobPost={jobPost}
        loggedInUserId={loggedInUserId}
        publicApplicationData={publicApplicationData}
      />
    </StyledModal>
  );
};

type ModalContentProps = {
  applicationSubmitModalVisibility: UseModalVisibilityHookReturn;
  jobPost: JobsBoardApplication;
  loggedInUserId: number;
  publicApplicationData: PublicApplication | undefined;
};
const ModalContent = ({
  applicationSubmitModalVisibility,
  jobPost,
  loggedInUserId,
  publicApplicationData,
}: ModalContentProps) => {
  const applicationQuestionsData = useApplicationQuestions(
    publicApplicationData,
    applicationSubmitModalVisibility
  );

  const { hasDeadline, formattedDeadline } = useMemo(
    () =>
      getApplicationDeadlineInfo(
        jobPost?.jobs_board_close_submissions_date_time,
        jobPost?.application_status,
        jobPost?.project_location?.country
      ),
    [
      jobPost?.jobs_board_close_submissions_date_time,
      jobPost?.application_status,
      jobPost?.project_location?.country,
    ]
  );

  return (
    <>
      <StyledHeader>
        <div className='button-placeholder' />
        <div className='title'>Application</div>
        <UnstyledButton onClick={applicationSubmitModalVisibility?.hideModal}>
          <Close />
        </UnstyledButton>
      </StyledHeader>
      <StyledBody>
        <PostHeader
          postRoleName={jobPost?.role?.name}
          postProjectName={jobPost?.project_name}
          postProjectLocation={jobPost?.project_location}
          postStatus={jobPost?.application_status}
          postCloseDate={jobPost?.jobs_board_close_submissions_date_time}
        />
        <SubmitJobQuestions
          loggedInUserId={loggedInUserId}
          publicApplicationData={publicApplicationData}
          applicationQuestionsData={applicationQuestionsData}
        />
      </StyledBody>
      <StyledFooter hasDeadline={hasDeadline}>
        {hasDeadline && (
          <div className='deadline-container'>
            <div className='icon-container'>
              <InfoCircleOutlined />
            </div>
            <div className='deadline-text'>
              Closes on {formattedDeadline || '-'}
            </div>
          </div>
        )}
        <SubmitButton
          onSubmitButtonClick={applicationQuestionsData.onSubmitButtonClick}
          areAnyFilesUploading={applicationQuestionsData.areAnyFilesUploading}
          isFormSubmitting={applicationQuestionsData.isSubmittingForm}
        />
      </StyledFooter>
    </>
  );
};

type SubmitButtonProps = {
  onSubmitButtonClick: () => void;
  areAnyFilesUploading: boolean;
  isFormSubmitting: boolean;
};
const SubmitButton = ({
  onSubmitButtonClick,
  areAnyFilesUploading,
  isFormSubmitting,
}: SubmitButtonProps) => {
  return (
    <Button
      type='primary'
      onClick={onSubmitButtonClick}
      className='apply-button'
      disabled={areAnyFilesUploading || isFormSubmitting}
    >
      Submit Application
    </Button>
  );
};

const StyledModal = styled(Modal)`
  &.ant-modal {
    max-height: none;
    max-width: none;
    height: 614px;
    width: 520px !important;

    @media (max-width: 768px) {
      width: 100dvw !important;
      height: 100dvh;
    }

    .ant-modal-content {
      height: 100%;
      width: 100%;
      max-height: none;
      max-width: none;
      border-radius: 4px;

      @media (max-width: 768px) {
        border-radius: 0;
      }

      .ant-modal-close {
        display: none;
      }

      .ant-modal-body {
        width: 100%;
        height: 100%;
        max-height: none;
        max-width: none;
        padding: 0;
        font-size: 12px;
        line-height: 16px;

        @media (max-width: 768px) {
          font-size: 16px;
          line-height: 24px;
        }
      }
    }
  }
`;

const StyledHeader = styled.div`
  padding: 16px 20px 15px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  display: flex;
  align-items: center;
  gap: 17px;

  @media (max-width: 768px) {
    padding: 8px 8px 7px;
    gap: 4px;
    justify-content: space-between;
  }

  .button-placeholder {
    display: none;

    @media (max-width: 768px) {
      display: block;
      width: 40px;
      height: 40px;
    }
  }

  .title {
    font-size: 17px;
    line-height: 21px;
    font-weight: 600;
    width: calc(100% - 17px - 16px);

    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 20px;
      width: auto;
    }
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;

    @media (max-width: 768px) {
      width: 40px;
      height: 40px;
    }

    svg {
      path {
        stroke: ${({ theme }) => theme.palette.stroke[3]};
      }

      @media (max-width: 768px) {
        width: 24px;
        height: 24px;
      }
    }
  }
`;

const StyledBody = styled.div`
  height: calc(100% - 53px - 64px);

  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;

  @media (max-width: 768px) {
    height: calc(100% - 56px - 120px);
    padding: 24px 20px 16px;
  }
`;

type StyledFooterProps = {
  hasDeadline: boolean;
};
const StyledFooter = styled.div<StyledFooterProps>`
  padding: 15px 20px 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  display: flex;
  justify-content: ${({ hasDeadline }) =>
    hasDeadline ? 'space-between' : 'flex-end'};
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 19px 20px 20px;
  }

  .deadline-container {
    display: flex;
    align-items: center;
    gap: 8px;

    .icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;

      svg {
        width: 15px;
        height: auto;
        fill: ${({ theme }) => theme.palette.primary[0]};
      }
    }

    .deadline-text {
      font-size: 14px;
      line-height: 20px;
    }
  }

  .apply-button {
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    padding: 6px 12px;

    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 24px;
      height: 48px;
    }
  }
`;
